import { Injectable } from '@angular/core';
import { LocalizationService } from './shared/services/localization.service';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { State } from 'src/app/state/app.state';
import { getLoggedInUserInfo } from 'src/app/state';
import { filter, finalize, map, Observable, of, shareReplay, switchMap, take, tap } from 'rxjs';

const BC_MODULE_ID = 1;
const LANG_ID_EN = 1;
const LANG_ID_FR = 2;
const LANG_ID_ES = 3;

@Injectable({providedIn: 'root'})
export class TranslationLoaderFacade {
    private translationsLoaded = false;
    private translationsLoading$?: Observable<void>;

    constructor(private localizationService: LocalizationService,
                private translate: TranslateService,
                private store: Store<State>) {

    }

    setLanguage(lang: string) {
        this.translate.use(lang);
      }

    public fetchTranslations() {
        this.ensureTranslationsLoaded().subscribe();
    }

    public fetchTranslationsAndWait(): Observable<void> {
        return this.ensureTranslationsLoaded();
    }

    public refreshTranslations(): Observable<void> {
        this.translationsLoaded = false;
        return this.ensureTranslationsLoaded(true);
    }

    private ensureTranslationsLoaded(forceReload = false): Observable<void> {
        if (this.translationsLoaded && !forceReload) {
            return of(void 0);
        }

        if (this.translationsLoading$ && !forceReload) {
            return this.translationsLoading$;
        }

        this.translationsLoading$ = this.store.select(getLoggedInUserInfo).pipe(
            filter(user => !!user),
            take(1),
            switchMap(user => {
                const { language, langId } = this.resolveLanguage(user!.preferredLanguage);
                this.translate.currentLang = language;
                return this.localizationService.fetchTranslations(BC_MODULE_ID, langId).pipe(
                    tap((translations) => {
                        this.translate.setTranslation(language, translations, true);
                        this.translate.use(language);
                        this.translationsLoaded = true;
                    }),
                    map(() => void 0)
                );
            }),
            finalize(() => {
                this.translationsLoading$ = undefined;
            }),
            shareReplay(1)
        );

        return this.translationsLoading$;
    }

    private resolveLanguage(preferredLanguage?: number): { language: string; langId: number } {
        if (preferredLanguage === LANG_ID_ES) {
            return { language: 'es-mx', langId: LANG_ID_ES };
        }

        return { language: 'en-US', langId: LANG_ID_EN };
    }
}